import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { projectsService } from '../../../services/partnerService';
import { useToast } from '../../../contexts/ToastContext';
import { supabase } from '../../../lib/supabase';

const STATUS_OPTIONS = ['Planning', 'In Progress', 'On Hold', 'Completed', 'Cancelled'];
const HEALTH_OPTIONS = ['On Track', 'At Risk', 'Delayed'];

const STATUS_COLORS = {
  planning: 'bg-secondary/10 text-secondary',
  'in progress': 'bg-primary/10 text-primary',
  'on hold': 'bg-slate-100 text-slate-600',
  completed: 'bg-success/10 text-success',
  cancelled: 'bg-error/10 text-error',
};

const HEALTH_COLORS = {
  'on track': 'bg-success/10 text-success',
  'at risk': 'bg-warning/10 text-warning',
  delayed: 'bg-error/10 text-error',
};

const ProjectDetailDrawer = ({ project, onClose, onUpdated, partners = [] }) => {
  const toast = useToast();
  const [mode, setMode] = useState('view'); // 'view' | 'edit' | 'delete'
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [form, setForm] = useState({
    name: project?.name || '',
    partner_id: project?.partner_id || '',
    client: project?.client || '',
    value: project?.rawValue || '',
    project_status: project?.status || 'Planning',
    health: project?.rawHealth || 'on-track',
    start_date: project?.rawStartDate || '',
    end_date: project?.rawEndDate || '',
    completion: project?.progress || 0,
    description: project?.description || '',
  });

  const handleChange = (e) => setForm(prev => ({ ...prev, [e?.target?.name]: e?.target?.value }));

  const handleSave = async () => {
    if (!form?.name?.trim()) { toast?.error('Project name is required.'); return; }
    setSaving(true);
    try {
      await projectsService?.update(project?.id, {
        name: form?.name,
        partner_id: form?.partner_id || null,
        client: form?.client,
        value: parseFloat(form?.value) || 0,
        project_status: form?.project_status,
        health: form?.health,
        start_date: form?.start_date || null,
        end_date: form?.end_date || null,
        completion: parseInt(form?.completion) || 0,
        description: form?.description,
      });
      toast?.success('Project updated successfully');
      onUpdated?.();
      setMode('view');
    } catch (err) {
      toast?.error(err?.message || 'Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { error } = await supabase?.from('projects')?.delete()?.eq('id', project?.id);
      if (error) throw error;
      toast?.success('Project deleted');
      onUpdated?.();
      onClose();
    } catch (err) {
      toast?.error(err?.message || 'Failed to delete project');
    } finally {
      setDeleting(false);
    }
  };

  if (!project) return null;

  const statusKey = (form?.project_status || project?.status || '')?.toLowerCase();
  const healthKey = (form?.health || project?.rawHealth || '')?.replace('-', ' ')?.toLowerCase();

  const healthLabel = form?.health === 'on-track' ? 'On Track' : form?.health === 'at-risk' ? 'At Risk' : form?.health === 'delayed' ? 'Delayed' : form?.health;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full z-50 w-full max-w-[520px] bg-white shadow-2xl flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-accent/5 to-primary/5 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent to-primary flex items-center justify-center flex-shrink-0">
              <Icon name="FolderOpen" size={18} className="text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-slate-500 font-medium">Active Project</p>
              <h2 className="text-base font-bold text-slate-900 leading-tight truncate max-w-[300px]">{project?.name}</h2>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {mode === 'view' && (
              <>
                <button onClick={() => setMode('edit')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors">
                  <Icon name="Pencil" size={13} /> Edit
                </button>
                <button onClick={() => setMode('delete')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-error/10 text-error text-xs font-semibold hover:bg-error/20 transition-colors">
                  <Icon name="Trash2" size={13} /> Delete
                </button>
              </>
            )}
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors">
              <Icon name="X" size={18} />
            </button>
          </div>
        </div>

        {/* Delete Confirmation */}
        {mode === 'delete' && (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div className="w-16 h-16 rounded-2xl bg-error/10 flex items-center justify-center mb-4">
              <Icon name="Trash2" size={28} className="text-error" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Delete Project?</h3>
            <p className="text-sm text-slate-500 mb-2">You are about to permanently delete:</p>
            <p className="text-sm font-semibold text-slate-800 mb-6 px-4 py-2 bg-slate-50 rounded-xl">{project?.name}</p>
            <p className="text-xs text-slate-400 mb-8">This action cannot be undone.</p>
            <div className="flex gap-3 w-full max-w-xs">
              <button onClick={() => setMode('view')} className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleting} className="flex-1 px-4 py-2.5 rounded-xl bg-error text-white text-sm font-semibold hover:bg-error/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {deleting ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Deleting...</> : <><Icon name="Trash2" size={14} /> Delete</>}
              </button>
            </div>
          </div>
        )}

        {/* View Mode */}
        {mode === 'view' && (
          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {/* Badges */}
            <div className="flex flex-wrap gap-2">
              <span className={`px-3 py-1 rounded-lg text-xs font-semibold ${STATUS_COLORS?.[statusKey] || 'bg-slate-100 text-slate-600'}`}>
                {project?.status}
              </span>
              <span className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1 ${HEALTH_COLORS?.[project?.health?.toLowerCase()] || 'bg-slate-100 text-slate-600'}`}>
                <Icon name={project?.health === 'On Track' ? 'CheckCircle2' : project?.health === 'At Risk' ? 'AlertTriangle' : 'XCircle'} size={11} />
                {project?.health}
              </span>
              <span className="px-3 py-1 rounded-lg text-xs font-semibold bg-slate-100 text-slate-500">{project?.projectId}</span>
            </div>

            {/* Key Info Grid */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: 'DollarSign', label: 'Value', value: project?.value || '—' },
                { icon: 'Users', label: 'Delivery Role', value: project?.deliveryRole || '—' },
                { icon: 'Calendar', label: 'Timeline', value: project?.timeline || 'TBD' },
                { icon: 'Building2', label: 'Client', value: project?.client || '—' },
              ]?.map((item, i) => (
                <div key={i} className="bg-slate-50 rounded-xl p-3.5">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Icon name={item?.icon} size={13} className="text-slate-400" />
                    <span className="text-xs text-slate-500 font-medium">{item?.label}</span>
                  </div>
                  <p className="text-sm font-semibold text-slate-900">{item?.value}</p>
                </div>
              ))}
            </div>

            {/* Progress Bar */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-600">Completion Progress</span>
                <span className="text-sm font-bold text-primary">{project?.progress}%</span>
              </div>
              <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${project?.health === 'On Track' ? 'bg-success' : project?.health === 'At Risk' ? 'bg-warning' : 'bg-error'}`}
                  style={{ width: `${project?.progress}%` }} />
              </div>
            </div>

            {/* Description */}
            {project?.description && (
              <div className="p-3.5 bg-slate-50 rounded-xl">
                <p className="text-xs text-slate-500 font-medium mb-1.5 flex items-center gap-1.5"><Icon name="FileText" size={13} />Description</p>
                <p className="text-sm text-slate-700 leading-relaxed">{project?.description}</p>
              </div>
            )}
          </div>
        )}

        {/* Edit Mode */}
        {mode === 'edit' && (
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {/* Name */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Project Name <span className="text-red-400">*</span></label>
              <input name="name" value={form?.name} onChange={handleChange} className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/50 transition-all" />
            </div>

            {/* Partner */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Partner</label>
              <select name="partner_id" value={form?.partner_id} onChange={handleChange} className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white transition-all">
                <option value="">— No Partner —</option>
                {partners?.map(p => <option key={p?.id} value={p?.id}>{p?.brand_name || p?.legal_name}</option>)}
              </select>
            </div>

            {/* Client */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Client</label>
              <input name="client" value={form?.client} onChange={handleChange} placeholder="Client name..." className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all" />
            </div>

            {/* Value */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Value ($)</label>
              <input name="value" type="number" min="0" value={form?.value} onChange={handleChange} className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all" />
            </div>

            {/* Status + Health */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Status</label>
                <select name="project_status" value={form?.project_status} onChange={handleChange} className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white transition-all">
                  {STATUS_OPTIONS?.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Health</label>
                <select name="health" value={form?.health} onChange={handleChange} className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 bg-white transition-all">
                  <option value="on-track">On Track</option>
                  <option value="at-risk">At Risk</option>
                  <option value="delayed">Delayed</option>
                </select>
              </div>
            </div>

            {/* Start + End Date */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Start Date</label>
                <input name="start_date" type="date" value={form?.start_date} onChange={handleChange} className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">End Date</label>
                <input name="end_date" type="date" value={form?.end_date} onChange={handleChange} className="w-full h-10 px-3 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all" />
              </div>
            </div>

            {/* Completion */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Completion (%) — {form?.completion}%</label>
              <input name="completion" type="range" min="0" max="100" value={form?.completion} onChange={handleChange} className="w-full accent-primary" />
              <div className="flex justify-between text-xs text-slate-400 mt-1"><span>0%</span><span>50%</span><span>100%</span></div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Description</label>
              <textarea name="description" value={form?.description} onChange={handleChange} rows={3} className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none transition-all" />
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-slate-100 bg-slate-50">
          {mode === 'view' && (
            <button onClick={onClose} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-white transition-colors">
              Close
            </button>
          )}
          {mode === 'edit' && (
            <div className="flex gap-3">
              <button onClick={() => setMode('view')} className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-white transition-colors">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-accent to-primary text-white text-sm font-semibold shadow-sm hover:shadow-md transition-all disabled:opacity-60 flex items-center justify-center gap-2">
                {saving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</> : <><Icon name="Save" size={14} /> Save Changes</>}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProjectDetailDrawer;
